import { type TypedDocumentNode, gql } from '@apollo/client';
import { useDrag } from '@use-gesture/react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { animated, useSprings } from 'react-spring';
import styled from 'styled-components';

import { DropCardInput } from '@sorare/core/src/__generated__/globalTypes';
import commonEssence from '@sorare/core/src/assets/rewards/common-essence.png';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { AddCards } from '@sorare/core/src/atoms/icons/AddCards';
import { DropCards } from '@sorare/core/src/atoms/icons/DropCards';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelS } from '@sorare/core/src/atoms/typography';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { CommonCoinsBalance } from '@sorare/core/src/components/commonGame/CommonCoinsBalance';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import { glossary } from '@sorare/core/src/lib/glossary';

import {
  BaseLayout,
  CardOrPackRelativeWrapper,
  CardWrapper,
  TopClearance,
} from '../RevealPackCards/ui';
import type {
  TinderCardSwipe_anyCardInterface,
  dropPackCardsMutation,
  dropPackCardsMutationVariables,
} from './__generated__/index.graphql';

const CardRelativeWrapper = styled(CardWrapper)`
  position: relative;
  pointer-events: none;
`;
const CardImgWrapper = styled(animated.div)`
  position: absolute;
  inset: 0;
  pointer-events: auto;
  user-select: none;
  touch-action: none;
  & > img {
    user-select: none;
    touch-action: none;
  }
`;
const CTAsWrapper = styled(SBHorizontal).attrs({ gap: 2 })`
  align-items: flex-start;
  width: calc(20 * var(--unit));
`;
const CoinsBalanceWrapper = styled.div`
  position: absolute;
  top: var(--unit);
  left: var(--unit);
`;
const EssenceIcon = styled.img`
  width: var(--intermediate-unit);
  height: var(--double-unit);
  object-fit: contain;
`;
const PackOpenedWrapper = styled(Horizontal).attrs({ center: true })`
  height: 100%;
`;
const BottomArea = styled(Vertical).attrs({ center: true, gap: 2 })`
  padding-top: var(--double-unit);
`;

const DROP_PACK_CARDS_MUTATION = gql`
  mutation dropPackCardsMutation($input: dropAndAddCardsInput!) {
    dropAndAddCards(input: $input) {
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<dropPackCardsMutation, dropPackCardsMutationVariables>;

type Props = {
  cards: TinderCardSwipe_anyCardInterface[];
  onContinue: () => void;
};
export const TinderCardSwipe = ({ cards, onContinue }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { formatMessage } = useIntl();
  const [commonCardPoints, setCommonCardPoints] = useState(
    currentUser?.commonCardPoints || 0
  );
  const [springStyles, api] = useSprings(cards.length, springIndex => ({
    x: 0,
    zIndex: -1 * springIndex,
  }));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [droppedCards, setDroppedCards] = useState<DropCardInput[]>([]);
  const [allCardsSeen, setAllCardsSeen] = useState(false);
  const isLaptopAndAbove = useIsLaptop();
  const currentCardPrice =
    cards.at(currentIndex)?.signedDropCardPrice?.price || 0;

  const containerWidth = isLaptopAndAbove ? 440 : window.innerWidth;
  const swipedCardFinalOffset = containerWidth + 200;

  const [dropCards, { loading }] = useMutation(DROP_PACK_CARDS_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  const moveToNextIndex = () => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
    if (currentIndex === cards.length - 1) {
      setAllCardsSeen(true);
    }
  };
  const dropCurrentCard = () => {
    const currentCard = cards[currentIndex];

    if (currentCard) {
      const { slug, signedDropCardPrice } = currentCard;

      if (signedDropCardPrice) {
        setCommonCardPoints(prev => prev + signedDropCardPrice.price);
        setDroppedCards(prev => [
          ...prev,
          { cardSlug: slug, signedPrice: signedDropCardPrice.signedPrice },
        ]);
      }
    }
  };

  const moveCardWithoutDrag = ({ isDrop }: { isDrop: boolean }) => {
    if (isDrop) {
      dropCurrentCard();
    }
    moveToNextIndex();

    const direction = isDrop ? -1 : 1;
    api.start(i => {
      if (currentIndex !== i) return {};

      return {
        x: direction * swipedCardFinalOffset,
        config: {
          tension: 100,
        },
      };
    });
  };

  const dropCardsAndContinue = async () => {
    await dropCards({ variables: { input: { droppedCards } } });
    onContinue();
  };
  const cardWidth = 320; //todo get actual card width

  const bind = useDrag(
    ({ args: [{ index }], movement: [mx], cancel, last }) => {
      if (Math.abs(mx) > cardWidth / 1.2) {
        cancel();
      }
      const isGone = Math.abs(mx) > cardWidth / 2;
      const direction = mx > 0 ? 1 : -1;

      api.start(i => {
        if (index !== i) return {};
        if (last && !isGone) {
          return {
            x: 0,
            config: {
              tension: 150,
            },
          };
        }
        const x = isGone ? swipedCardFinalOffset * direction : mx;

        if (isGone && currentIndex === i) {
          if (direction < 0) {
            dropCurrentCard();
          }
          moveToNextIndex();
        }

        return {
          x,
          config: {
            tension: isGone ? 150 : 800,
            friction: 50,
          },
        };
      });
    }
  );

  return (
    <BaseLayout>
      <TopClearance>
        <CoinsBalanceWrapper>
          <CommonCoinsBalance commonCardPoints={commonCardPoints} withoutLink />
        </CoinsBalanceWrapper>
      </TopClearance>
      <CardOrPackRelativeWrapper>
        <CardRelativeWrapper>
          {allCardsSeen ? (
            <PackOpenedWrapper center>
              <HeadlineM>
                <FormattedMessage
                  id="TinderCardSwipe.packOpened"
                  defaultMessage="Pack Opened"
                />
              </HeadlineM>
            </PackOpenedWrapper>
          ) : (
            cards.map((card, index) => {
              return (
                <CardImgWrapper
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  {...bind({ index })}
                  style={springStyles[index]}
                >
                  <CardImg
                    width={320}
                    src={card.pictureUrl}
                    draggable={false}
                    alt={card.name}
                  />
                </CardImgWrapper>
              );
            })
          )}
        </CardRelativeWrapper>
      </CardOrPackRelativeWrapper>
      <BottomArea>
        <CTAsWrapper>
          <Vertical center>
            <IconButton
              color="red"
              onClick={() => {
                moveCardWithoutDrag({ isDrop: true });
              }}
            >
              <DropCards />
            </IconButton>
            <LabelS className="text-center">
              <FormattedMessage
                id="TinderCardSwipe.dropFor"
                defaultMessage="Drop for {amount} {essenceIcon}"
                values={{
                  amount: currentCardPrice,
                  essenceIcon: (
                    <EssenceIcon
                      src={commonEssence}
                      alt={formatMessage(glossary.commonEssence)}
                    />
                  ),
                }}
              />
            </LabelS>
          </Vertical>
          <IconButton
            color="secondary"
            onClick={() => {
              moveCardWithoutDrag({ isDrop: false });
            }}
          >
            <AddCards />
          </IconButton>
        </CTAsWrapper>
        {allCardsSeen && (
          <LoadingButton
            color="secondary"
            onClick={dropCardsAndContinue}
            size="medium"
            loading={loading}
          >
            <FormattedMessage {...glossary.continue} />
          </LoadingButton>
        )}
      </BottomArea>
    </BaseLayout>
  );
};

TinderCardSwipe.fragments = {
  anyCardInterface: gql`
    fragment TinderCardSwipe_anyCardInterface on AnyCardInterface {
      slug
      pictureUrl
      name
      signedDropCardPrice {
        signedPrice
        price
      }
    }
  ` as TypedDocumentNode<TinderCardSwipe_anyCardInterface>,
};
